import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Cookies from 'js-cookie';
import {
  Alert,
  Button,
  Form,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
} from 'reactstrap';
import { injectIntl } from 'react-intl';
import _ from 'lodash';

import {
  isLoggedIn,
} from '../../api/Auth';
import {
  APIcreateDownload,
  APIcreateDownloadRequest,
  APIgetDownloadSummary,
  APIgetDownloadRequestSummary,
} from '../../api/Downloads';
import { APIcreateShareDownload } from '../../api/Shares';
import { APIcreatePageDownload } from '../../api/Pages';

import {
  scrollModalToError,
  sendTrackingEvent,
} from '../../helpers';
import { findPathToKey } from '../../helpers/App';
import {
  getDownloadReasons,
  shouldShowDownloadReasons,
  translateAnswerTree,
} from '../../helpers/Download';
import { userHasFeature } from '../../helpers/Features';
import {
  hasResizableImages,
  hasConvertableImages,
  getFileSizeDescription,
  getTargetFileFormats,
} from '../../helpers/Files';
import {
  addFlashMessage,
  removeFlashMessage,
} from '../../helpers/FlashMessages';
import { isValidEmail } from '../../helpers/Validators';

import DownloadInfo from './Info';
import DownloadReasonSelector from './ReasonsSelector';
import ProcessingSpinner from '../ProcessingSpinner';
import UpgradePlanButton from '../UpgradePlanButton';
import LikeWhatYouSee from '../LikeWhatYouSee';
import ErrorBoundary from '../ErrorBoundary';
import Loading from '../Loading';
import HelpIcon from '../HelpIcon';
import ModalTabHeader from '../ModalTabHeader';

class DownloadModal extends Component {
  constructor(props) {
    super(props);

    const shouldShowReason = shouldShowDownloadReasons(props.share, props.files, props.collections);

    this.state = {
      activeTab: props.request || shouldShowReason ? 'reason' : 'default',
      view: '',
      selectedFileSizes: ['original'],
      selectedFileFormats: ['original'],
      error: null,
      saving: false,
      waiting: false,
      downloadUrl: null,
      summary: {
        collections: 0,
        files: 0,
        resizableFiles: 0,
        links: 0,
        size: 0,
        resizableSize: 0,
      },
      summaryLoading: true,
      name: '',
      email: '',
      nameValidationOK: false,
      emailValidationOK: false,
      longWait: false,
      reason: shouldShowReason,
      answerTree: {},
      showAnswerTreeErrors: false,
    };

    this.handleChangeFileSizes = this.handleChangeFileSizes.bind(this);
    this.handleChangeFileFormats = this.handleChangeFileFormats.bind(this);
    this.handleDownload = this.handleDownload.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleAnswerTree = this.handleAnswerTree.bind(this);
    this.validateReason = this.validateReason.bind(this);
    this.hasOneEdition = this.hasOneEdition.bind(this);
  }

  componentDidMount() {
    const {
      request,
      requireName,
      requireEmail,
      profile,
      shareUuid,
      pageId,
      share,
      files,
      collections,
    } = this.props;

    const shouldShowReason = shouldShowDownloadReasons(share, files, collections);
    const isPageDownload = !!pageId && !request;

    if (isPageDownload) {
      this.setState({ view: 'preparing' });
    } else if (
      !request && !shouldShowReason &&
      this.hasOneEdition() &&
      !((requireName || requireEmail) &&
      !isLoggedIn())
    ) {
      this.setState({ view: 'preparing' });
    } else {
      this.setState({ view: 'main' });
    }

    this.initSelectedFileSizes();
    this.getDownloadSummary();

    if (request) {
      sendTrackingEvent('download-request-modal-open');
    } else {
      sendTrackingEvent('download-modal-open');
    }

    if (requireName) {
      const nameCookie = Cookies.get(`sp_share_user_name_${shareUuid}`);

      if (isLoggedIn()) {
        this.setState({
          name: profile.name,
          nameValidationOK: true,
        });
      } else if (nameCookie) {
        this.setState({
          name: nameCookie,
          nameValidationOK: true,
        });
      } else {
        this.setState({
          nameValidationOK: false,
        });
      }
    } else {
      this.setState({
        nameValidationOK: true,
      });
    }

    if (requireEmail) {
      const emailCookie = Cookies.get(`sp_share_user_email_${shareUuid}`);

      if (isLoggedIn()) {
        this.setState({
          email: profile.email,
          emailValidationOK: true,
        });
      } else if (emailCookie) {
        this.setState({
          email: emailCookie,
          emailValidationOK: true,
        });
      } else {
        this.setState({
          emailValidationOK: false,
        });
      }
    } else {
      this.setState({
        emailValidationOK: true,
      });
    }
  }

  getAvailablefileSizes() {
    const {
      fileSizes,
      availableFileSizes,
      share,
    } = this.props;

    if (fileSizes) {
      if (share && share.select_file_sizes) {
        return fileSizes.filter(fs => (share.file_sizes.includes(`${fs.id}`)));
      }

      if (availableFileSizes && availableFileSizes.length > 0) {
        return fileSizes.filter(fs => (availableFileSizes.includes(`${fs.id}`)));
      }
    }

    return fileSizes || [];
  }

  getFileSize(fileSizeId) {
    if (fileSizeId === 'original') {
      return {
        id: 'original',
      };
    }

    const { fileSizes } = this.props;
    const fileSize = fileSizes.find(fs => (`${fs.id}` === `${fileSizeId}`));
    if (fileSize) {
      return fileSize;
    }

    return false;
  }

  getDownloadSummary() {
    const {
      share,
      collections,
      files,
      versions,
      shareUuid,
      pageId,
      requestUuid,
      request,
      fileCollectionMap,
      requireName,
      requireEmail,
      intl,
    } = this.props;

    setTimeout(() => {
      this.setState({ longWait: true });
    }, 5000);

    if (request) {
      const collectionKeys = collections.map(c => (c.uuid || c.id)).filter(Boolean);
      const fileKeys = files.map(f => (f.uuid || f.id)).filter(Boolean);

      APIgetDownloadRequestSummary({
        collections: collectionKeys,
        files: fileKeys,
      }).then((response) => {
        this.setState({
          summary: {
            collections: response.data.collections,
            files: response.data.files,
            resizableFiles: response.data.resizable_files,
            links: response.data.links,
            size: response.data.size,
            resizableSize: response.data.resizable_size,
          },
          summaryLoading: false,
          longWait: false,
        });
      });
    } else {
      const collectionKeys = collections.map(c => (c.uuid || c.id)).filter(Boolean);
      const fileKeys = files.map(f => (f.uuid || f.id)).filter(Boolean);

      APIgetDownloadSummary({
        collections: collectionKeys,
        files: fileKeys,
        versions,
        shareUuid,
        pageId,
        requestUuid,
        fileCollectionMap,
      }).then((response) => {
        const shouldShowReason = shouldShowDownloadReasons(share, files, collections);
        const isPageDownload = !!pageId && !request;

        const summaryData = {
          collections: response.data.collections,
          files: response.data.files,
          allFiles: response.data.all_files,
          resizableFiles: response.data.resizable_files,
          downloadableFiles: response.data.downloadable_files,
          links: response.data.links,
          size: response.data.size,
          resizableSize: response.data.resizable_size,
        };

        this.setState({
          summary: summaryData,
          summaryLoading: false,
          longWait: false,
        });

        if (isPageDownload) {
          const nonLinkFiles = (summaryData.files || 0) - (summaryData.links || 0);
          if (nonLinkFiles > 0) {
            this.handleDownload();
          } else {
            this.setState({
              view: 'done',
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>.{br}You probably don't have permissions to download selected files.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          }
          return;
        }

        if (
          !request && !shouldShowReason &&
          this.hasOneEdition() && !((requireName || requireEmail) && !isLoggedIn())) {
          this.handleDownload();
        }
      });
    }
  }

  hasOneEdition() {
    const { files } = this.props;
    return files.length === 1 && files[0].parent_uuid !== null;
  }

  setStatusDone() {
    setTimeout(() => {
      this.setState({
        saving: false,
        waiting: false,
        view: 'done',
      });
    }, 1000);
  }

  showFileSizes() {
    const {
      files,
      collections,
      downloadAll,
      requestUuid,
    } = this.props;

    return (downloadAll || collections.length > 0 || hasResizableImages(files) || requestUuid);
  }

  showFileFormats() {
    const {
      files,
      collections,
      downloadAll,
      requestUuid,
    } = this.props;

    return (downloadAll || collections.length > 0 || hasConvertableImages(files) || requestUuid);
  }

  initSelectedFileSizes() {
    const {
      availableFileSizes,
      share,
    } = this.props;

    if (availableFileSizes) {
      this.setState({ selectedFileSizes: availableFileSizes });
    } else if (share && share.select_file_sizes && !share.file_sizes.includes('original') && this.getAvailablefileSizes().length > 0) {
      this.setState({ selectedFileSizes: [`${this.getAvailablefileSizes()[0].id}`] });
    }
  }

  isFileSizeChecked(fileSizeId) {
    const { selectedFileSizes } = this.state;
    return selectedFileSizes.find(sfs => (sfs === `${fileSizeId}`)) || false;
  }

  isFileFormatChecked(fileFormat) {
    const { selectedFileFormats } = this.state;
    return selectedFileFormats.find(sfs => (sfs === `${fileFormat}`)) || false;
  }

  handleChangeFileSizes(e) {
    const {
      value,
      checked,
    } = e.target;

    if (checked) {
      this.setState(prevState => ({
        selectedFileSizes: [...prevState.selectedFileSizes, `${value}`],
      }));
    } else if (this.showFileSizes()) {
      this.setState(prevState => ({
        selectedFileSizes: prevState.selectedFileSizes.filter(sfs => (sfs !== `${value}`)),
      }));
    }
  }

  handleChangeFileFormats(e) {
    const {
      value,
      checked,
    } = e.target;

    if (checked) {
      this.setState(prevState => ({
        selectedFileFormats: [...prevState.selectedFileFormats, `${value}`],
      }));
    } else if (this.showFileFormats()) {
      this.setState(prevState => ({
        selectedFileFormats: prevState.selectedFileFormats.filter(sff => (sff !== `${value}`)),
      }));
    }
  }

  processDownloadResponse(response) {
    const { intl, handleClose } = this.props;

    if (response.url) {
      const downloadLink = document.createElement('a');
      downloadLink.setAttribute('href', response.url);
      downloadLink.setAttribute('id', response.uuid);
      if (response.file_name) {
        downloadLink.setAttribute('download', response.file_name);
      } else {
        downloadLink.setAttribute('download', response.url.split('/').pop().split('?')[0]);
      }
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      this.setState({
        downloadUrl: {
          url: response.url,
          fileName: downloadLink.getAttribute('download'),
        },
      });

      removeFlashMessage(response.uuid);
    }

    if (response.status === 'error') {
      removeFlashMessage(response.uuid);
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'Download failed. Please try again.' }),
      });
    }

    if (response.status === 'empty') {
      removeFlashMessage(response.uuid);
      handleClose();
      addFlashMessage({
        type: 'warning',
        text: intl.formatMessage({
          defaultMessage: `<strong>Your download was empty</strong>.{br}You Probably you don't have permissions to download selected files.`,
        }, {
          strong: str => <strong>{str}</strong>,
          br: <br />,
        }),
      });
    }
  }

  validateReason() {
    const { answerTree } = this.state;
    this.setState({ showAnswerTreeErrors: true });

    const errorPath = findPathToKey(answerTree, 'requiredError');

    if (errorPath && _.get(answerTree, errorPath)) {
      scrollModalToError('modal-download');
      return;
    }

    this.setState({ activeTab: 'default' });
  }

  handleDownload() {
    const {
      intl,
      files,
      versions,
      collections,
      request,
      requestUuid,
      shareUuid,
      pageId,
      fileCollectionMap,
      prepareDownload,
      prepareDownloadRequest,
      prepareShareDownload,
      preparePageDownload,
    } = this.props;
    const {
      selectedFileSizes,
      selectedFileFormats,
      name,
      email,
      reason,
      answerTree,
    } = this.state;
    const fileSizes = [];
    let translatedReasonTree = null;

    if (request || reason) {
      translatedReasonTree = translateAnswerTree(answerTree);
    }

    const collectionUuids = collections.map(c => (c.uuid || c.id)).filter(Boolean);

    this.setState({
      error: null,
      saving: true,
      waiting: true,
      view: 'preparing',
    });
    selectedFileSizes.forEach((fileSizeId) => {
      const fileSize = this.getFileSize(fileSizeId);
      if (fileSize) {
        fileSizes.push({ id: fileSize.id });
      }
    });

    if (shareUuid) {
      prepareShareDownload(
        shareUuid,
        files,
        collectionUuids,
        fileSizes,
        selectedFileFormats,
        versions,
        name,
        email,
        reason ? translatedReasonTree : null,
      ).then((response) => {
        const {
          uuid,
          status,
        } = response.data;
        if (uuid) {
          this.processDownloadResponse(response.data);
          sendTrackingEvent('download-complete', {
            files: files.length,
            collections: collectionUuids.length,
            share: shareUuid,
          });
        }
        if (status === 'empty') {
          if (collections.length > 0) {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>.{br}Probably selected Collections are empty.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          } else {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>`,
              }, {
                strong: str => <strong>{str}</strong>,
              }),
            });
          }
        }
        this.setStatusDone();
      })
        .catch(error => {
          this.setState({
            error: error.response.data.errors,
            saving: false,
            waiting: false,
            view: 'main',
          });
        });
    } else if (pageId) {
      preparePageDownload(
        pageId,
        files,
        collectionUuids,
        fileSizes,
        selectedFileFormats,
        versions,
        fileCollectionMap,
        reason ? translatedReasonTree : null,
      ).then((response) => {
        const {
          uuid,
          status,
        } = response.data;
        if (uuid) {
          this.processDownloadResponse({
            ...response.data,
            url: `/downloads/${uuid}/download`,
          });
          sendTrackingEvent('download-complete', {
            files: files.length,
            collections: collectionUuids.length,
            page: pageId,
          });
        }
        if (status === 'empty') {
          if (collections.length > 0) {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>.{br}Probably selected Collections are empty.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          } else {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>`,
              }, {
                strong: str => <strong>{str}</strong>,
              }),
            });
          }
        }
        this.setStatusDone();
      })
        .catch(error => {
          this.setState({
            error: error.response?.data?.errors || error.response?.data?.message || error.message,
            saving: false,
            waiting: false,
            view: 'main',
          });
        });
    } else if (request) {
      prepareDownloadRequest(
        files,
        collectionUuids,
        fileSizes,
        translatedReasonTree,
      ).then((response) => {
        const {
          uuid,
          status,
        } = response.data;
        if (uuid) {
          sendTrackingEvent('download-request-complete', {
            files: files.length,
            collections: collectionUuids.length,
          });
        }
        if (status === 'empty') {
          if (collections.length > 0) {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download request was empty</strong>.{br}
                  You probably don't have permissions to send download requests for files
                  inside the selected Collection or the Collection is empty.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          } else {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download request was empty</strong>.{br}
                  You probably don't have permissions to send download requests for selected files.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          }
        }
        this.setStatusDone();
      });
    } else {
      prepareDownload(
        files,
        collectionUuids,
        fileSizes,
        selectedFileFormats,
        versions,
        requestUuid,
        reason ? translatedReasonTree : null,
      ).then((response) => {
        const {
          uuid,
          status,
        } = response.data;
        if (uuid) {
          this.processDownloadResponse(response.data);
          sendTrackingEvent('download-complete', {
            files: files.length,
            collections: collectionUuids.length,
          });
        }
        if (status === 'empty') {
          if (collections.length > 0) {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>.{br}
                  You probably don't have permissions to download files inside the
                  selected Collection or the Collection is empty.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          } else {
            this.setState({
              error: intl.formatMessage({
                defaultMessage: `<strong>Your download was empty</strong>.{br}
                  You probably don't have permissions to download selected files.`,
              }, {
                strong: str => <strong>{str}</strong>,
                br: <br />,
              }),
            });
          }
        }
        this.setStatusDone();
      });
    }
  }

  filesAddedAfterRequest() {
    const {
      collections,
      request,
      pageId,
    } = this.props;

    if (request || pageId) {
      return false;
    }

    let added = false;
    collections.forEach((c) => {
      const capabilities = Array.isArray(c.capabilities) ? c.capabilities : [];
      if (
        capabilities.includes('download_files_and_collections') &&
        (capabilities.includes('request_download_files_and_collections') && c.request)
      ) {
        added = true;
      }
    });

    return added;
  }

  handleNameChange(e) {
    this.setState({
      name: e.target.value,
    });

    if (e.target.value.length > 0) {
      this.setState({
        nameValidationOK: true,
      });
    } else {
      this.setState({
        nameValidationOK: false,
      });
    }
  }

  handleEmailChange(e) {
    this.setState({
      email: e.target.value,
    });

    if (isValidEmail(e.target.value)) {
      this.setState({
        emailValidationOK: true,
      });
    } else {
      this.setState({
        emailValidationOK: false,
      });
    }
  }

  handleAnswerTree(answerTree) {
    this.setState({ answerTree });
  }

  render() {
    const {
      intl,
      share,
      request,
      availableFileSizes,
      handleClose,
      requireName,
      requireEmail,
    } = this.props;
    const {
      activeTab,
      view,
      selectedFileSizes,
      selectedFileFormats,
      error,
      saving,
      waiting,
      downloadUrl,
      summary,
      summaryLoading,
      name,
      email,
      nameValidationOK,
      emailValidationOK,
      longWait,
      reason,
      answerTree,
      showAnswerTreeErrors,
    } = this.state;
    const files = summary.files - summary.links;
    const notDownloadableFiles = typeof summary.downloadableFiles === 'number' ?
      Math.max(0, summary.allFiles - summary.downloadableFiles) : 0;

    const nameCookie = share ? Cookies.get(`sp_share_user_name_${share.uuid}`) : null;
    const emailCookie = share ? Cookies.get(`sp_share_user_email_${share.uuid}`) : null;

    const reasons = getDownloadReasons(request);

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {request ? intl.formatMessage({ defaultMessage: 'Download request' }) :
            intl.formatMessage({ defaultMessage: 'Download' })}
        </ModalHeader>
        {(!view || view === 'main') && (
          <ModalTabHeader
            show={!!request || reason}
            tabs={[
              { index: 'reason', label: intl.formatMessage({ defaultMessage: 'File usage' }) },
              { index: 'default', label: intl.formatMessage({ defaultMessage: 'Size & Format' }) },
            ]}
            activeTab={activeTab}
          />
        )}
        {(!view || view === 'main') && (
          <>
            {(activeTab === 'reason') && (
              <ModalBody>
                <Form className="reason-form">
                  <DownloadReasonSelector
                    reasons={reasons}
                    answerTree={answerTree}
                    handleAnswerTree={this.handleAnswerTree}
                    showAnswerTreeErrors={showAnswerTreeErrors}
                  />
                </Form>
              </ModalBody>
            )}
            {activeTab === 'default' && (
              <ModalBody>
                {this.filesAddedAfterRequest() && (
                  <Alert color="warning">
                    {intl.formatMessage({
                      defaultMessage: `Additional images have been added to this Collection.
                        Please submit an additional request to download for approval.`,
                    })}
                  </Alert>
                )}
                {(!summaryLoading && summary.links > 0) && (
                  <p className={classnames('center', { error: files === 0 })}>
                    {intl.formatMessage({
                      defaultMessage: `{files, plural, =0 {No files} one {One file} other
                      {{files} files}} will be downloaded.`,
                    }, { files })}
                    <span className="link-cards-warning">
                      {intl.formatMessage({ defaultMessage: 'Link cards are not downloadable.' })}
                    </span>
                  </p>
                )}
                {(!summaryLoading && notDownloadableFiles > 0) && (
                  <Alert color="warning">
                    {intl.formatMessage({
                      defaultMessage: `{count, plural, one {One file} other {{count} files}}
                        can't be downloaded because you don't have permission to download
                        {count, plural, one {it} other {them}}.`,
                    }, { count: notDownloadableFiles })}
                  </Alert>
                )}
                {requireName && !isLoggedIn() && !nameCookie && (
                  <div className="inputNameSection">
                    <Label for="inpName">
                      {intl.formatMessage({ defaultMessage: 'Enter your name to download' })}
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      id="inpName"
                      placeholder={intl.formatMessage({ defaultMessage: 'Name' })}
                      value={name}
                      onChange={this.handleNameChange}
                      invalid={error && Boolean(error.email)}
                    />
                      {error && error.name && (
                        <FormFeedback style={{ display: 'block' }}>
                          {error.name}
                        </FormFeedback>
                      )}
                  </div>
                )}
                {requireEmail && !isLoggedIn() && !emailCookie && (
                  <div className="inputEmailSection">
                    <Label for="inpEmail">
                      {intl.formatMessage({ defaultMessage: 'Enter your email to download' })}
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      id="inpEmail"
                      placeholder={intl.formatMessage({ defaultMessage: 'Email address' })}
                      value={email}
                      onChange={this.handleEmailChange}
                      invalid={error && Boolean(error.email)}
                    />
                    {error && error.email && (
                      <FormFeedback style={{ display: 'block' }}>
                        {error.email}
                      </FormFeedback>
                    )}
                  </div>
                )}
                <p>
                  {intl.formatMessage({ defaultMessage: 'Select sizes *' })}
                </p>
                <Form className="download-form">
                  <ul className="file-sizes-list">
                    {(
                      (!availableFileSizes || availableFileSizes.includes('original')) &&
                      (!share || !share.select_file_sizes || share.file_sizes.includes('original'))
                    ) && (
                      <li>
                        <div className="checkbox-wrapper">
                          <Input
                            type="checkbox"
                            className="input-check check-l"
                            id="file-size-0"
                            value="original"
                            checked={this.isFileSizeChecked('original')}
                            onChange={this.handleChangeFileSizes}
                          />
                        </div>
                        <div className="checkbox-label">
                          <Label htmlFor="file-size-0">
                            {intl.formatMessage({ defaultMessage: 'Original size' })}
                          </Label>
                        </div>
                      </li>
                    )}
                    {this.showFileSizes() && (
                      this.getAvailablefileSizes().map(fs => (
                        <li key={fs.id}>
                          <div className="checkbox-wrapper">
                            {(userHasFeature('file-sizes') || share) ? (
                              <Input
                                type="checkbox"
                                className="input-check check-l"
                                id={`file-size-${fs.id}`}
                                value={fs.id}
                                checked={this.isFileSizeChecked(fs.id)}
                                onChange={this.handleChangeFileSizes}
                              />
                            ) : (
                              <div className="checkbox-wrapper-upgrade checkbox-wrapper-upgrade-l">
                                <UpgradePlanButton
                                  feature="file-sizes"
                                  simple={true}
                                />
                              </div>
                            )}
                          </div>
                          <div className="checkbox-label">
                            <Label htmlFor={`file-size-${fs.id}`}>
                              {fs.name}
                              <span>{getFileSizeDescription(fs)}</span>
                            </Label>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                  {this.showFileFormats() && (
                    <>
                      <p>
                        {intl.formatMessage({ defaultMessage: 'Export as *' })}
                        <HelpIcon permanent tooltip url="https://help.stockpress.co/en/articles/11832408-convert-and-export-files-to-different-formats">
                          {intl.formatMessage({ defaultMessage: 'Convert files to the following available formats' })}
                        </HelpIcon>
                      </p>
                      <ul className="file-sizes-list">
                        <li>
                          <div className="checkbox-wrapper">
                            <Input
                              type="checkbox"
                              className="input-check check-l"
                              id="file-format-original"
                              value="original"
                              checked={selectedFileFormats.includes('original')}
                              onChange={this.handleChangeFileFormats}
                            />
                          </div>
                          <div className="checkbox-label">
                            <Label htmlFor="file-format-original">
                              {intl.formatMessage({ defaultMessage: 'Original format' })}
                            </Label>
                          </div>
                        </li>
                        {getTargetFileFormats().map(f => (
                          <li key={f}>
                            <div className="checkbox-wrapper">
                              {(userHasFeature('file-formats') || share) ? (
                                <Input
                                  type="checkbox"
                                  className="input-check check-l"
                                  id={`file-format-${f}`}
                                  value={f}
                                  checked={selectedFileFormats.includes(f)}
                                  onChange={this.handleChangeFileFormats}
                                />
                              ) : (
                                <div className="checkbox-wrapper-upgrade checkbox-wrapper-upgrade-l">
                                  <UpgradePlanButton
                                    feature="file-formats"
                                    simple={true}
                                  />
                                </div>
                              )}
                            </div>
                            <div className="checkbox-label">
                              <Label htmlFor={`file-format-${f}`}>
                                {f.toUpperCase()}
                              </Label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Form>
              </ModalBody>
            )}
            {(summaryLoading && longWait) && (
              <div className="download-estimated-size">
                {intl.formatMessage({
                  defaultMessage: 'We need more time to prepare this download - please wait...',
                })}
              </div>
            )}
            <DownloadInfo
              summary={summary}
              summaryLoading={summaryLoading}
              isDownloadRequest={!!request}
              isOriginalSizeChecked={!!(this.isFileSizeChecked('original') || !this.showFileSizes())}
              isSizeChecked={this.showFileSizes() && selectedFileSizes.filter(sf => (sf !== 'original')).length > 0}
              isOriginalFormatChecked={!!(this.isFileFormatChecked('original') || !this.showFileFormats())}
              isFormatChecked={this.showFileFormats() && selectedFileFormats.filter(sf => (sf !== 'original')).length > 0}
            />
          </>
        )}

        {(!view || view === 'main') && (
          <ModalFooter className="justify-sb">
            <div>
              {(!!request || reason) && activeTab !== 'reason' && (
                <Button
                  color="link"
                  onClick={() => this.setState({ activeTab: 'reason' })}
                >
                  {intl.formatMessage({ defaultMessage: 'Back' })}
                </Button>
              )}
            </div>
            <div className="buttons-group">
              <Button
                color="secondary"
                disabled={saving}
                onClick={handleClose}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              {activeTab === 'reason' && (
                <Button
                  color="primary"
                  onClick={this.validateReason}
                >
                  {intl.formatMessage({ defaultMessage: 'Continue' })}
                </Button>
              )}
              {activeTab === 'default' && (
                <Button
                  color="primary"
                  disabled={
                  saving ||
                  summary.files === 0 ||
                  summary.size === 0 ||
                  summaryLoading ||
                  selectedFileSizes.length === 0 ||
                  selectedFileFormats.length === 0 ||
                  !nameValidationOK ||
                  !emailValidationOK
                }
                  onClick={this.handleDownload}
                >
                  {summaryLoading ? (
                    <>
                      <ProcessingSpinner show={true} />
                      {intl.formatMessage({ defaultMessage: 'Processing...' })}
                    </>
                  ) : (
                    <>
                      {request ? (
                        <>
                          <ProcessingSpinner show={saving || waiting} />
                          {intl.formatMessage({ defaultMessage: 'Request download' })}
                        </>
                      ) : (
                        <>
                          <ProcessingSpinner show={saving || waiting} />
                          {intl.formatMessage({ defaultMessage: 'Download' })}
                        </>
                      )}
                    </>
                  )}
                </Button>
              )}
            </div>
          </ModalFooter>
        )}

        {view === 'preparing' && (
          <ModalBody>
            {request ? (
              <p className="center">
                {intl.formatMessage({
                  defaultMessage: 'We are preparing your download request. This may take a while.',
                })}
              </p>
            ) : (
              <p className="center">
                {intl.formatMessage({
                  defaultMessage: 'We are preparing your download. This may take a while.',
                })}
              </p>
            )}
            <Loading />
          </ModalBody>
        )}

        {view === 'done' && (
          <ModalBody>
            {error ? (
              <p className="error center">{error}</p>
            ) : (
              <>
                {request ? (
                  <>
                    <p className="center">
                      {intl.formatMessage({
                        defaultMessage: `Thank you! Your download request has been sent.
                          You will get a notification when it has been processed.`,
                      })}
                    </p>
                    <p className="center">
                      {intl.formatMessage({
                        defaultMessage: `You can check the status of your download requests <link>here</link>.`,
                      }, {
                        link: str => <Link to="/download-requests/" onClick={handleClose}>{str}</Link>,
                      })}
                    </p>
                  </>
                ) : (
                  <>
                    {selectedFileFormats.filter(sf => (sf !== 'original')).length > 0 ? (
                      <>
                        <h2 className="center">
                          <i className="fal fa-hourglass-half fa-spin fa-spin-reverse" style={{ '--fa-animation-duration': '2s', '--fa-animation-iteration-count': 3 }} />
                        </h2>
                        <p className="center">
                          {intl.formatMessage({
                            defaultMessage: 'Your download may still be processing.{br}Exporting different files formats can take a little extra time.',
                          }, { br: <br /> })}
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="center green">
                          <i className="fal fa-circle-check" />
                        </h2>
                        <p className="center">
                          {intl.formatMessage({ defaultMessage: 'Your download is ready and should start automatically.' })}<br />
                          {(downloadUrl && downloadUrl.url) && (
                            <span>
                              {intl.formatMessage({
                                defaultMessage: '<link>Click here</link> if it did not.',
                              }, {
                                link: str => <a href={downloadUrl.url} id="download-modal-link" download={downloadUrl.fileName}>{str}</a>,
                              })}
                            </span>
                          )}
                        </p>
                      </>
                    )}
                  </>
                )}
                {!isLoggedIn() && <LikeWhatYouSee />}
              </>
            )}
          </ModalBody>
        )}
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = () => ({
  prepareDownload: (
    files,
    collections,
    fileSizes,
    fileFormats,
    versions,
    requestUuid = null,
    reason = null,
  ) => (
    APIcreateDownload({
      files: files.map(f => (f.uuid)),
      collections,
      file_sizes: fileSizes,
      file_formats: fileFormats,
      request_uuid: requestUuid,
      versions,
      reason,
    })
  ),
  prepareDownloadRequest: (files, collections, fileSizes, reason) => (
    APIcreateDownloadRequest({
      files: files.map(f => (f.uuid)),
      collections,
      file_sizes: fileSizes,
      reason,
    })
  ),
  prepareShareDownload: (
    shareUuid,
    files,
    collections,
    fileSizes,
    fileFormats,
    versions,
    name,
    email,
    reason = null,
  ) => (
    APIcreateShareDownload(shareUuid, {
      files: files.map(f => (f.uuid)),
      collections,
      file_sizes: fileSizes,
      file_formats: fileFormats,
      versions,
      name,
      email,
      reason,
    })
  ),
  preparePageDownload: (
    pageId,
    files,
    collections,
    fileSizes,
    fileFormats,
    versions,
    fileCollectionMap = {},
    reason = null,
  ) => (
    APIcreatePageDownload(pageId, {
      files: files.map(f => (f.uuid)),
      collections,
      file_sizes: fileSizes,
      file_formats: fileFormats,
      versions,
      file_collection_map: fileCollectionMap,
      reason,
    })
  ),
});

const mapStateToProps = state => ({
  share: state.Shares.share,
  profile: state.Auth.profile,
  fileSizes: state.App.fileSizes,
});

DownloadModal.propTypes = {
  intl: PropTypes.object.isRequired,
  share: PropTypes.object,
  fileSizes: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  prepareDownload: PropTypes.func.isRequired,
  prepareDownloadRequest: PropTypes.func.isRequired,
  prepareShareDownload: PropTypes.func.isRequired,
  preparePageDownload: PropTypes.func.isRequired,
  files: PropTypes.array,
  collections: PropTypes.array,
  shareUuid: PropTypes.string,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  downloadAll: PropTypes.bool,
  request: PropTypes.bool,
  requestUuid: PropTypes.string,
  fileCollectionMap: PropTypes.object,
  availableFileSizes: PropTypes.array,
  versions: PropTypes.array,
  requireName: PropTypes.bool,
  requireEmail: PropTypes.bool,
  profile: PropTypes.object,
};

DownloadModal.defaultProps = {
  share: null,
  files: [],
  collections: [],
  shareUuid: null,
  pageId: null,
  downloadAll: false,
  request: false,
  requestUuid: null,
  fileCollectionMap: {},
  availableFileSizes: null,
  versions: [],
  requireName: false,
  requireEmail: false,
  profile: null,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DownloadModal));
